import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  LinearProgress,
  IconButton,
  Tooltip,
  TablePagination,
} from "@mui/material";
import { ReactComponent as DeleteIcon } from "../../asserts/logo/delete.svg";
import { ReactComponent as SortArrowIcon } from "../../asserts/logo/ArrowDown.svg";
import {
  CARD_FONT,
  PORTFOLIO_TOOLTIP_SLOT_PROPS,
  resolveAppStatusDisplay,
  resolveProgressBarColor,
} from "./portfolioShared";

const TABLE_HEAD_CELL_SX = {
  fontFamily: CARD_FONT,
  fontWeight: 700,
  fontSize: { xs: 12, sm: 12.5, md: 13 },
  lineHeight: "20px",
  letterSpacing: 0,
  color: "#383838",
  borderBottom: "1px solid #E6E6E6",
  py: { xs: 1.25, sm: 1.5 },
  px: { xs: 1, sm: 1.5, md: 2 },
  whiteSpace: "nowrap",
  bgcolor: "#FFFFFF",
  verticalAlign: "middle",
  boxSizing: "border-box",
};

const TABLE_BODY_CELL_SX = {
  fontFamily: CARD_FONT,
  fontWeight: 400,
  fontSize: { xs: 12.5, sm: 13, md: 13.5 },
  lineHeight: "20px",
  letterSpacing: 0,
  color: "#344054",
  borderBottom: "1px solid #F2F4F7",
  py: { xs: 1.25, sm: 1.5 },
  px: { xs: 1, sm: 1.5, md: 2 },
  verticalAlign: "middle",
  boxSizing: "border-box",
};

const SELECT_COL_WIDTH = 50;
const APPLICATION_COL_WIDTH = 240;
const ACTIONS_COL_WIDTH = 76;

/** Sticky Header Cells */
const STICKY_HEAD_SELECT_SX = {
  position: "sticky",
  left: 0,
  zIndex: 10,
  width: SELECT_COL_WIDTH,
  minWidth: SELECT_COL_WIDTH,
  maxWidth: SELECT_COL_WIDTH,
  bgcolor: "#FFFFFF",
  borderBottom: "1px solid #E6E6E6",
  px: { xs: 0.75, sm: 1.25 },
  textAlign: "center",
  boxSizing: "border-box",
};

const STICKY_HEAD_APPLICATION_SX = {
  position: "sticky",
  left: SELECT_COL_WIDTH,
  zIndex: 10,
  width: APPLICATION_COL_WIDTH,
  minWidth: APPLICATION_COL_WIDTH,
  maxWidth: APPLICATION_COL_WIDTH,
  bgcolor: "#FFFFFF",
  borderBottom: "1px solid #E6E6E6",
  boxSizing: "border-box",
};

const STICKY_HEAD_ACTIONS_SX = {
  position: "sticky",
  right: 0,
  zIndex: 10,
  width: ACTIONS_COL_WIDTH,
  minWidth: ACTIONS_COL_WIDTH,
  maxWidth: ACTIONS_COL_WIDTH,
  bgcolor: "#FFFFFF",
  borderBottom: "1px solid #E6E6E6",
  textAlign: "center",
  boxSizing: "border-box",
};

/** Sticky Body Cells */
const STICKY_BODY_SELECT_SX = {
  position: "sticky",
  left: 0,
  zIndex: 4,
  width: SELECT_COL_WIDTH,
  minWidth: SELECT_COL_WIDTH,
  maxWidth: SELECT_COL_WIDTH,
  px: { xs: 0.75, sm: 1.25 },
  textAlign: "center",
  boxSizing: "border-box",
};

const STICKY_BODY_APPLICATION_SX = {
  position: "sticky",
  left: SELECT_COL_WIDTH,
  zIndex: 4,
  width: APPLICATION_COL_WIDTH,
  minWidth: APPLICATION_COL_WIDTH,
  maxWidth: APPLICATION_COL_WIDTH,
  boxSizing: "border-box",
};

const STICKY_BODY_ACTIONS_SX = {
  position: "sticky",
  right: 0,
  zIndex: 4,
  width: ACTIONS_COL_WIDTH,
  minWidth: ACTIONS_COL_WIDTH,
  maxWidth: ACTIONS_COL_WIDTH,
  textAlign: "center",
  boxSizing: "border-box",
};

const RISK_TIER_RANK = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function getSortValue(app, field) {
  switch (field) {
    case "application":
      return (app.project_name || "").toLowerCase();
    case "riskTier":
      return RISK_TIER_RANK[String(app.risk_level || "").toUpperCase()] || 0;
    case "riskScore": {
      const num = toNumber(app.risk_score);
      return num === null ? -1 : num;
    }
    case "readiness": {
      const num = toNumber(app.ready_score);
      return num === null ? -1 : num;
    }
    case "testProgress":
      return app.test_total > 0 ? app.test_progress / app.test_total : 0;
    case "openFlags":
      return Number(app.flag_count) || 0;
    case "status":
      return resolveAppStatusDisplay(app).label.toLowerCase();
    default:
      return "";
  }
}

function sortApps(apps, sortField, sortDirection) {
  if (!sortField) return apps;
  const dir = sortDirection === "asc" ? 1 : -1;
  return [...apps].sort((a, b) => {
    const av = getSortValue(a, sortField);
    const bv = getSortValue(b, sortField);
    if (typeof av === "number" && typeof bv === "number") {
      if (av === bv) return 0;
      return (av - bv) * dir;
    }
    return String(av).localeCompare(String(bv)) * dir;
  });
}

function SortableHeader({ label, direction, onClick, align = "left" }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label={`Sort by ${label} ${direction === "asc" ? "ascending" : "descending"}`}
      sx={{
        border: 0,
        bgcolor: "transparent",
        p: 0,
        m: 0,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: align === "center" ? "center" : "flex-start",
        gap: 0.5,
        fontFamily: CARD_FONT,
        fontWeight: 700,
        fontSize: { xs: 12, sm: 12.5, md: 13 },
        lineHeight: "20px",
        letterSpacing: 0,
        color: "#383838",
        width: align === "center" ? "100%" : "auto",
      }}
    >
      {label}
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 16,
          height: 16,
          flexShrink: 0,
          opacity: 1,
          transform: direction === "asc" ? "rotate(180deg)" : "none",
          transition: "transform 0.15s ease",
          "& svg": {
            width: 16,
            height: 16,
            display: "block",
            opacity: 1,
          },
          "& path": {
            fill: "none",
            stroke: "#667085",
            strokeWidth: 1.33333,
          },
        }}
      >
        <SortArrowIcon aria-hidden="true" />
      </Box>
    </Box>
  );
}

/** Risk Tier badges with system colors */
const RISK_TIER_FLAG_SX = {
  CRITICAL: {
    width: 73,
    bgcolor: "#DE350B",
    color: "#FFFFFF",
    label: "CRITICAL",
  },
  HIGH: {
    width: 52,
    bgcolor: "#FFD2C7",
    color: "#B82500",
    label: "HIGH",
  },
  MEDIUM: {
    width: 72,
    bgcolor: "#FFE999",
    color: "#8A5C00",
    label: "MEDIUM",
  },
  LOW: {
    width: 48,
    bgcolor: "#E8FCF3",
    color: "#00754E",
    label: "LOW",
  },
};

function RiskTierBadge({ riskKey }) {
  const key = String(riskKey || "MEDIUM").toUpperCase();
  const style = RISK_TIER_FLAG_SX[key] || RISK_TIER_FLAG_SX.MEDIUM;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: style.width,
        height: { xs: 22, sm: 24 },
        borderRadius: "12px",
        px: { xs: "6px", sm: "8px" },
        boxSizing: "border-box",
        bgcolor: style.bgcolor,
        fontFamily: CARD_FONT,
        fontWeight: 700,
        fontSize: { xs: "11px", sm: "12px" },
        lineHeight: { xs: "22px", sm: "24px" },
        color: style.color,
        whiteSpace: "nowrap",
      }}
    >
      {style.label}
    </Box>
  );
}

const DEFAULT_ROWS_PER_PAGE = 10;
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const TABLE_PAGINATION_SX = {
  borderTop: "1px solid #E6E6E6",
  fontFamily: CARD_FONT,
  color: "#707070",
  ".MuiTablePagination-toolbar": {
    minHeight: { xs: 46, sm: 52 },
    px: { xs: 1, sm: 2 },
    flexWrap: "wrap",
    gap: 0.5,
  },
  ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
    fontFamily: CARD_FONT,
    fontSize: { xs: 12, sm: 13 },
    lineHeight: "20px",
    color: "#707070",
    m: 0,
  },
  ".MuiTablePagination-select": {
    fontFamily: CARD_FONT,
    fontSize: { xs: 12, sm: 13 },
    lineHeight: "20px",
    color: "#383838",
  },
  ".MuiTablePagination-actions": {
    color: "#383838",
    "& .Mui-disabled": { color: "#BBBBBB" },
    ml: { xs: 0.5, sm: 1.5 },
  },
};

export default function PortfolioTableView({ apps, onRowClick, onDelete, onBulkDelete, canDelete }) {
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [sortField, setSortField] = useState("riskScore");
  const [sortDirection, setSortDirection] = useState("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  useEffect(() => {
    setSelectedIds((prev) => {
      const appIds = new Set(apps.map((app) => app.id));
      const next = new Set([...prev].filter((id) => appIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [apps]);

  const sortedApps = useMemo(
    () => sortApps(apps, sortField, sortDirection),
    [apps, sortField, sortDirection]
  );

  const totalRows = sortedApps.length;
  const maxPage = Math.max(0, Math.ceil(totalRows / rowsPerPage) - 1);

  useEffect(() => {
    if (page > maxPage) setPage(maxPage);
  }, [page, maxPage]);

  useEffect(() => {
    setPage(0);
  }, [apps.length, sortField, sortDirection, rowsPerPage]);

  const paginatedApps = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedApps.slice(start, start + rowsPerPage);
  }, [sortedApps, page, rowsPerPage]);

  const allPageSelected = paginatedApps.length > 0 && paginatedApps.every((app) => selectedIds.has(app.id));
  const somePageSelected = paginatedApps.some((app) => selectedIds.has(app.id));

  const selectedApps = useMemo(
    () => apps.filter((app) => selectedIds.has(app.id)),
    [apps, selectedIds]
  );
  const selectedCount = selectedApps.length;

  const handleSort = (key) => {
    if (sortField === key) {
      setSortDirection((prev) => (prev === "desc" ? "asc" : "desc"));
      return;
    }
    setSortField(key);
    setSortDirection("desc");
  };

  const allSelected = allPageSelected;
  const someSelected = somePageSelected;

  const toggleAll = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        paginatedApps.forEach((app) => next.delete(app.id));
        return next;
      });
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      paginatedApps.forEach((app) => next.add(app.id));
      return next;
    });
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const columns = useMemo(
    () => [
      { key: "select", label: "", sortable: false, width: SELECT_COL_WIDTH, align: "center" },
      { key: "application", label: "Application", sortable: true, width: APPLICATION_COL_WIDTH, align: "left" },
      { key: "riskTier", label: "Risk Tier", sortable: true, minWidth: 105, align: "left" },
      { key: "riskScore", label: "Risk Score", sortable: true, minWidth: 100, align: "center" },
      { key: "readiness", label: "Readiness", sortable: true, minWidth: 100, align: "center" },
      { key: "testProgress", label: "Test Progress", sortable: true, minWidth: 175, align: "left" },
      { key: "openFlags", label: "Open Flags", sortable: true, minWidth: 100, align: "center" },
      { key: "status", label: "Status", sortable: true, minWidth: 125, align: "left" },
      { key: "actions", label: "Actions", sortable: false, width: ACTIONS_COL_WIDTH, align: "center" },
    ],
    []
  );

  const stickyHeadSx = (key) => {
    if (key === "select") return STICKY_HEAD_SELECT_SX;
    if (key === "application") return STICKY_HEAD_APPLICATION_SX;
    if (key === "actions") return STICKY_HEAD_ACTIONS_SX;
    return null;
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        border: "1px solid #E6E6E6",
        borderRadius: { xs: "8px", sm: "12px" },
        bgcolor: "#FFFFFF",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {selectedCount > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: { xs: 1.5, sm: 2 },
            py: { xs: 1, sm: 1.25 },
            borderBottom: "1px solid #E6E6E6",
            bgcolor: "#FFFFFF",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontFamily: CARD_FONT,
              fontWeight: 400,
              fontSize: { xs: 12.5, sm: 13.5 },
              lineHeight: "20px",
              color: "#707070",
            }}
          >
            {selectedCount} item{selectedCount !== 1 ? "s" : ""} selected
          </Typography>
          {canDelete && onBulkDelete ? (
            <Box
              component="button"
              type="button"
              onClick={() => onBulkDelete(selectedApps)}
              aria-label={`Delete ${selectedCount} selected projects`}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                border: 0,
                bgcolor: "transparent",
                cursor: "pointer",
                p: 0.5,
                color: "#DE350B",
                fontFamily: CARD_FONT,
                fontWeight: 700,
                fontSize: { xs: 12.5, sm: 13.5 },
                lineHeight: "20px",
                "&:hover": { opacity: 0.85 },
              }}
            >
              <DeleteIcon style={{ width: 16, height: 18, display: "block" }} />
              Delete
            </Box>
          ) : null}
        </Box>
      )}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          bgcolor: "#FFFFFF",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          maxWidth: "100%",
          width: "100%",
          minWidth: 0,
          borderRadius: 0,
          boxSizing: "border-box",
          scrollbarWidth: "thin",
          scrollbarColor: "#EAECF0 transparent",
          "&::-webkit-scrollbar": {
            height: { xs: 5, sm: 6 },
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#EAECF0",
            borderRadius: "12px",
            minWidth: 80,
          },
        }}
      >
        <Table
          sx={{
            minWidth: { xs: 900, sm: 980, md: 1060 },
            width: "100%",
            borderCollapse: "separate",
            borderSpacing: 0,
            tableLayout: "auto",
          }}
          aria-label="App portfolio table"
        >
          <TableHead>
            <TableRow sx={{ bgcolor: "#FFFFFF" }}>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align || "left"}
                  sx={{
                    ...TABLE_HEAD_CELL_SX,
                    width: col.width,
                    minWidth: col.minWidth || col.width,
                    maxWidth: col.width,
                    textAlign: col.align || "left",
                    ...stickyHeadSx(col.key),
                  }}
                >
                  {col.key === "select" ? (
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected && !allSelected}
                      onChange={toggleAll}
                      sx={{ p: 0.5, color: "#707070", "&.Mui-checked": { color: "#005BF0" } }}
                    />
                  ) : col.sortable ? (
                    <SortableHeader
                      label={col.label}
                      direction={sortField === col.key ? sortDirection : "desc"}
                      onClick={() => handleSort(col.key)}
                      align={col.align || "left"}
                    />
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedApps.map((app) => {
              const riskKey = String(app.risk_level || "MEDIUM").toUpperCase();
              const status = resolveAppStatusDisplay(app);
              const progressPct =
                app.test_total > 0
                  ? Math.min(100, (app.test_progress / app.test_total) * 100)
                  : 0;
              const progressColor = resolveProgressBarColor(app, status);

              return (
                <TableRow
                  key={app.id}
                  hover
                  selected={selectedIds.has(app.id)}
                  sx={{
                    cursor: "pointer",
                    bgcolor: "#FFFFFF",
                    "&:last-child td": { borderBottom: 0 },
                    "& td": { bgcolor: "#FFFFFF" },
                    "&:hover td": { bgcolor: "#F9FAFB !important" },
                    "&.Mui-selected td": { bgcolor: "#F8FAFF !important" },
                    "&.Mui-selected:hover td": { bgcolor: "#F0F6FF !important" },
                  }}
                >
                  {/* Column 1: Checkbox (Sticky Left) */}
                  <TableCell
                    sx={{ ...TABLE_BODY_CELL_SX, ...STICKY_BODY_SELECT_SX }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      size="small"
                      checked={selectedIds.has(app.id)}
                      onChange={() => toggleRow(app.id)}
                      sx={{ p: 0.5, color: "#707070", "&.Mui-checked": { color: "#005BF0" } }}
                    />
                  </TableCell>

                  {/* Column 2: Application (Sticky Left) */}
                  <TableCell
                    sx={{ ...TABLE_BODY_CELL_SX, ...STICKY_BODY_APPLICATION_SX }}
                    onClick={() => onRowClick(app)}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        minWidth: 0,
                        width: "100%",
                      }}
                    >
                      <Tooltip
                        title={app.project_name || ""}
                        arrow
                        placement="bottom-start"
                        enterDelay={300}
                        componentsProps={PORTFOLIO_TOOLTIP_SLOT_PROPS}
                      >
                        <Typography
                          component="span"
                          sx={{
                            fontFamily: CARD_FONT,
                            fontWeight: 700,
                            fontSize: { xs: "12.5px", sm: "13px", md: "13.5px" },
                            lineHeight: { xs: "18px", sm: "20px" },
                            color: "#005BF0",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "block",
                            maxWidth: "100%",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {app.project_name}
                        </Typography>
                      </Tooltip>
                      {app.subtitle ? (
                        <Typography
                          sx={{
                            fontFamily: CARD_FONT,
                            fontSize: { xs: "10px", sm: "11px" },
                            lineHeight: "16px",
                            color: "#98A2B3",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                          }}
                        >
                          {app.subtitle}
                        </Typography>
                      ) : null}
                    </Box>
                  </TableCell>

                  {/* Column 3: Risk Tier */}
                  <TableCell sx={TABLE_BODY_CELL_SX} onClick={() => onRowClick(app)}>
                    {app.risk_level ? <RiskTierBadge riskKey={riskKey} /> : "—"}
                  </TableCell>

                  {/* Column 4: Risk Score */}
                  <TableCell align="center" sx={{ ...TABLE_BODY_CELL_SX, textAlign: "center" }} onClick={() => onRowClick(app)}>
                    <Typography sx={{ fontFamily: CARD_FONT, fontSize: { xs: 12.5, sm: 13.5 }, color: "#344054", textAlign: "center" }}>
                      {app.risk_score != null && app.risk_score !== "" ? app.risk_score : "—"}
                    </Typography>
                  </TableCell>

                  {/* Column 5: Readiness */}
                  <TableCell align="center" sx={{ ...TABLE_BODY_CELL_SX, textAlign: "center" }} onClick={() => onRowClick(app)}>
                    <Typography sx={{ fontFamily: CARD_FONT, fontSize: { xs: 12.5, sm: 13.5 }, color: "#344054", textAlign: "center" }}>
                      {app.ready_score != null && app.ready_score !== "" ? app.ready_score : "—"}
                    </Typography>
                  </TableCell>

                  {/* Column 6: Test Progress */}
                  <TableCell sx={TABLE_BODY_CELL_SX} onClick={() => onRowClick(app)}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: { xs: 1, sm: 1.5 },
                        minWidth: { xs: 130, sm: 150, md: 160 },
                        width: "100%",
                      }}
                    >
                      <LinearProgress
                        variant="determinate"
                        value={progressPct}
                        sx={{
                          flex: 1,
                          minWidth: { xs: 50, sm: 65, md: 70 },
                          height: { xs: 5, sm: 6 },
                          borderRadius: 999,
                          bgcolor: "#F0F0F0",
                          "& .MuiLinearProgress-bar": { bgcolor: progressColor, borderRadius: 999 },
                        }}
                      />
                      <Typography
                        sx={{
                          fontFamily: CARD_FONT,
                          fontSize: { xs: 12, sm: 12.5, md: 13 },
                          lineHeight: "20px",
                          color: "#555555",
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                        }}
                      >
                        {app.test_progress || 0} of {app.test_total || 0}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Column 7: Open Flags */}
                  <TableCell align="center" sx={{ ...TABLE_BODY_CELL_SX, textAlign: "center" }} onClick={() => onRowClick(app)}>
                    <Typography sx={{ fontFamily: CARD_FONT, fontSize: { xs: 12.5, sm: 13.5 }, color: "#344054", textAlign: "center" }}>
                      {app.flag_count != null ? app.flag_count : 0}
                    </Typography>
                  </TableCell>

                  {/* Column 8: Status */}
                  <TableCell sx={TABLE_BODY_CELL_SX} onClick={() => onRowClick(app)}>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: { xs: "8px", sm: "10px" },
                        py: { xs: "2px", sm: "3px" },
                        borderRadius: "12px",
                        bgcolor:
                          status.kind === "success"
                            ? "#E8F5EF"
                            : status.kind === "danger"
                            ? "#FEE2E2"
                            : status.kind === "running"
                            ? "#EFF8FF"
                            : "#F2F4F7",
                        color: status.color,
                        fontFamily: CARD_FONT,
                        fontSize: { xs: "11px", sm: "12px" },
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {status.label}
                    </Box>
                  </TableCell>

                  {/* Column 9: Actions (Sticky Right) */}
                  <TableCell
                    align="center"
                    sx={{ ...TABLE_BODY_CELL_SX, ...STICKY_BODY_ACTIONS_SX }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                      {canDelete && onDelete ? (
                        <Tooltip
                          title="Delete"
                          arrow
                          placement="bottom"
                          enterDelay={0}
                          componentsProps={PORTFOLIO_TOOLTIP_SLOT_PROPS}
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => onDelete(e, app)}
                            aria-label="Delete project"
                            sx={{
                              p: 0.5,
                              color: "#DE350B",
                              "&:hover": { bgcolor: "rgba(222, 53, 11, 0.08)" },
                            }}
                          >
                            <DeleteIcon style={{ width: 16, height: 18, display: "block" }} />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Table Pagination Bar */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          borderTop: "1px solid #E6E6E6",
          minHeight: { xs: 46, sm: 52 },
          bgcolor: "#FFFFFF",
          p: { xs: 0.5, sm: 0 },
        }}
      >
        <Box sx={{ flex: 1, pl: { xs: 1, sm: 2 }, py: { xs: 0.5, sm: 0 }, minWidth: 0 }}>
          {selectedCount > 0 ? (
            <Typography
              sx={{
                fontFamily: CARD_FONT,
                fontWeight: 400,
                fontSize: { xs: 12, sm: 13 },
                lineHeight: "20px",
                color: "#707070",
              }}
            >
              {selectedCount} row{selectedCount !== 1 ? "s" : ""} selected
            </Typography>
          ) : null}
        </Box>
        <TablePagination
          component="div"
          count={totalRows}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
          sx={{
            ...TABLE_PAGINATION_SX,
            borderTop: 0,
            flexShrink: 0,
          }}
        />
      </Box>
    </Box>
  );
}
